import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CreditCard, ShoppingCart, Plus, Minus, Trash2, User, Receipt } from "lucide-react";
import ReceiptModal from "@/components/ReceiptModal";

const methods = [["cash", "Efectivo"], ["card", "Tarjeta"], ["transfer", "Transferencia"]];

export default function POS() {
  const [tab, setTab] = useState("plan");
  const [members, setMembers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [products, setProducts] = useState([]);
  const [sales, setSales] = useState([]);
  const [method, setMethod] = useState("cash");
  // plan cobro
  const [memberId, setMemberId] = useState("");
  const [planId, setPlanId] = useState("");
  // product cobro
  const [cart, setCart] = useState([]); // {id, name, price, qty}
  const [prodMember, setProdMember] = useState("");
  const [busy, setBusy] = useState(false);
  const [receiptId, setReceiptId] = useState(null);
  const [docType, setDocType] = useState("receipt");
  const [clientRtn, setClientRtn] = useState("");
  const [clientName, setClientName] = useState("");

  const loadSales = () => api.get("/sales?limit=15").then((r) => setSales(r.data));
  useEffect(() => {
    api.get("/members").then((r) => setMembers(r.data));
    api.get("/plans").then((r) => setPlans(r.data));
    api.get("/products").then((r) => setProducts(r.data));
    loadSales();
  }, []);

  const selectedPlan = plans.find((p) => p.id === planId);

  const addToCart = (p) => {
    setCart((c) => {
      const ex = c.find((i) => i.id === p.id);
      if (ex) return c.map((i) => (i.id === p.id ? { ...i, qty: i.qty + 1 } : i));
      return [...c, { id: p.id, name: p.name, price: p.price, qty: 1 }];
    });
  };
  const setQty = (id, delta) => setCart((c) => c.map((i) => (i.id === id ? { ...i, qty: Math.max(1, i.qty + delta) } : i)));
  const removeItem = (id) => setCart((c) => c.filter((i) => i.id !== id));
  const cartTotal = cart.reduce((s, i) => s + i.price * i.qty, 0);

  const cobrarPlan = async () => {
    if (!memberId) return toast.error("Selecciona un socio");
    if (!planId) return toast.error("Selecciona un plan");
    setBusy(true);
    try {
      const r = await api.post("/sales", { type: "plan", member_id: memberId, plan_id: planId, payment_method: method, document_type: docType, client_rtn: clientRtn, client_name: clientName });
      toast.success("Membresía cobrada y renovada");
      setMemberId(""); setPlanId(""); loadSales();
      if (r.data?.id) setReceiptId(r.data.id);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const cobrarProductos = async () => {
    if (cart.length === 0) return toast.error("El carrito está vacío");
    setBusy(true);
    try {
      const r = await api.post("/sales", {
        type: "product", member_id: prodMember,
        items: cart.map((i) => ({ product_id: i.id, qty: i.qty })), payment_method: method,
        document_type: docType, client_rtn: clientRtn, client_name: clientName,
      });
      toast.success(`Venta registrada · $${cartTotal.toFixed(2)}`);
      setCart([]); setProdMember(""); loadSales();
      api.get("/products").then((r2) => setProducts(r2.data));
      if (r.data?.id) setReceiptId(r.data.id);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <p className="overline text-[#00f0ff]">Punto de venta</p>
      <h1 className="font-display text-5xl mb-8">COBRO</h1>

      <div className="flex gap-2 mb-6">
        <button data-testid="pos-tab-plan" onClick={() => setTab("plan")} className={`flex items-center gap-2 px-5 py-2.5 text-sm border ${tab === "plan" ? "border-[#00f0ff] text-[#00f0ff]" : "border-white/10 text-white/50 hover:text-white"}`}><CreditCard className="w-4 h-4" /> Membresía / Plan</button>
        <button data-testid="pos-tab-product" onClick={() => setTab("product")} className={`flex items-center gap-2 px-5 py-2.5 text-sm border ${tab === "product" ? "border-[#00f0ff] text-[#00f0ff]" : "border-white/10 text-white/50 hover:text-white"}`}><ShoppingCart className="w-4 h-4" /> Productos</button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          {tab === "plan" ? (
            <div className="bg-[#0a0a0a] border border-white/10 p-6 space-y-4" data-testid="pos-plan-panel">
              <div><label className="overline block mb-1.5">Socio *</label>
                <select data-testid="pos-plan-member" value={memberId} onChange={(e) => setMemberId(e.target.value)} className="w-full bg-[#121212] border border-white/10 px-3 py-2.5 text-sm focus:border-[#00f0ff] focus:outline-none">
                  <option value="">Seleccionar socio...</option>
                  {members.map((m) => <option key={m.id} value={m.id}>{m.first_name} {m.last_name}</option>)}
                </select></div>
              <div><label className="overline block mb-1.5">Plan *</label>
                <select data-testid="pos-plan-select" value={planId} onChange={(e) => setPlanId(e.target.value)} className="w-full bg-[#121212] border border-white/10 px-3 py-2.5 text-sm focus:border-[#00f0ff] focus:outline-none">
                  <option value="">Seleccionar plan...</option>
                  {plans.map((p) => <option key={p.id} value={p.id}>{p.name} · ${p.price} · {p.duration_days}d</option>)}
                </select></div>
            </div>
          ) : (
            <div data-testid="pos-product-panel">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
                {products.map((p) => (
                  <button key={p.id} data-testid={`pos-product-${p.id}`} onClick={() => addToCart(p)} disabled={p.track_stock && p.stock <= 0}
                    className="text-left bg-[#0a0a0a] border border-white/10 p-4 hover:border-[#00f0ff] transition-colors disabled:opacity-40">
                    <p className="font-semibold text-sm truncate">{p.name}</p>
                    <p className="font-display text-2xl text-[#39FF14]">${p.price?.toFixed(2)}</p>
                    <p className="text-xs text-white/40">{p.track_stock ? `Stock: ${p.stock}` : "Sin control"}</p>
                  </button>
                ))}
              </div>
              <div className="bg-[#0a0a0a] border border-white/10 p-4">
                <p className="overline mb-3">Carrito</p>
                {cart.length === 0 && <p className="text-white/30 text-sm">Toca un producto para agregarlo.</p>}
                {cart.map((i) => (
                  <div key={i.id} className="flex items-center justify-between py-2 border-b border-white/5">
                    <span className="text-sm">{i.name}</span>
                    <div className="flex items-center gap-2">
                      <button onClick={() => setQty(i.id, -1)} className="p-1 border border-white/20 hover:bg-white/10"><Minus className="w-3 h-3" /></button>
                      <span className="w-6 text-center font-mono-logs">{i.qty}</span>
                      <button onClick={() => setQty(i.id, 1)} className="p-1 border border-white/20 hover:bg-white/10"><Plus className="w-3 h-3" /></button>
                      <span className="w-16 text-right text-[#39FF14] font-mono-logs">${(i.price * i.qty).toFixed(2)}</span>
                      <button onClick={() => removeItem(i.id)} className="p-1 text-[#FF3B30] hover:bg-white/10"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                ))}
                <div className="mt-3">
                  <label className="overline block mb-1.5"><User className="w-3 h-3 inline mr-1" />Socio (opcional)</label>
                  <select data-testid="pos-product-member" value={prodMember} onChange={(e) => setProdMember(e.target.value)} className="w-full bg-[#121212] border border-white/10 px-3 py-2 text-sm focus:border-[#00f0ff] focus:outline-none">
                    <option value="">Cliente de mostrador</option>
                    {members.map((m) => <option key={m.id} value={m.id}>{m.first_name} {m.last_name}</option>)}
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="bg-[#0a0a0a] border border-white/10 p-6 h-fit lg:sticky lg:top-6">
          <p className="overline mb-2">Total a cobrar</p>
          <p data-testid="pos-total" className="font-display text-6xl text-[#39FF14] mb-6">${(tab === "plan" ? (selectedPlan?.price || 0) : cartTotal).toFixed(2)}</p>
          <label className="overline block mb-2">Método de pago</label>
          <div className="grid grid-cols-3 gap-2 mb-6">
            {methods.map(([v, l]) => (
              <button key={v} data-testid={`pos-method-${v}`} onClick={() => setMethod(v)} className={`py-2 text-xs border ${method === v ? "border-[#00f0ff] text-[#00f0ff]" : "border-white/10 text-white/50"}`}>{l}</button>
            ))}
          </div>
          <div className="mb-6">
            <label className="overline block mb-2">Comprobante</label>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" data-testid="pos-doc-receipt" onClick={() => setDocType("receipt")} className={`py-2 text-xs border ${docType === "receipt" ? "border-[#00f0ff] text-[#00f0ff]" : "border-white/10 text-white/50"}`}>Recibo simple</button>
              <button type="button" data-testid="pos-doc-invoice" onClick={() => setDocType("invoice")} className={`py-2 text-xs border ${docType === "invoice" ? "border-[#00f0ff] text-[#00f0ff]" : "border-white/10 text-white/50"}`}>Factura CAI</button>
            </div>
            {docType === "invoice" && (
              <div className="mt-3 space-y-2" data-testid="pos-invoice-fields">
                <input data-testid="pos-client-rtn" value={clientRtn} onChange={(e) => setClientRtn(e.target.value)} placeholder="RTN del cliente (opcional)" className="w-full bg-[#121212] border border-white/10 px-3 py-2 text-sm focus:border-[#00f0ff] focus:outline-none" />
                <input data-testid="pos-client-name" value={clientName} onChange={(e) => setClientName(e.target.value)} placeholder="Nombre / razón social" className="w-full bg-[#121212] border border-white/10 px-3 py-2 text-sm focus:border-[#00f0ff] focus:outline-none" />
              </div>
            )}
          </div>
          <button data-testid="pos-charge-button" disabled={busy} onClick={tab === "plan" ? cobrarPlan : cobrarProductos}
            className="w-full bg-[#00f0ff] text-black font-bold uppercase tracking-wide py-3 hover:bg-[#00c2cc] disabled:opacity-50">
            {busy ? "Procesando..." : "Cobrar"}
          </button>
        </div>
      </div>

      <div className="mt-8">
        <p className="overline mb-3">Ventas recientes</p>
        <div className="bg-[#0a0a0a] border border-white/10 overflow-x-auto">
          <table className="w-full text-sm" data-testid="pos-sales-table">
            <thead><tr className="text-left overline border-b border-white/10">
              <th className="px-4 py-3">Fecha</th><th className="px-4 py-3">Tipo</th><th className="px-4 py-3">Cliente</th>
              <th className="px-4 py-3">Detalle</th><th className="px-4 py-3">Método</th><th className="px-4 py-3 text-right">Total</th><th className="px-4 py-3 text-right">Recibo</th>
            </tr></thead>
            <tbody>
              {sales.length === 0 && <tr><td colSpan="7" className="px-4 py-8 text-center text-white/30">Sin ventas aún.</td></tr>}
              {sales.map((s) => (
                <tr key={s.id} className="border-b border-white/5 hover:bg-white/5">
                  <td className="px-4 py-3 font-mono-logs text-xs">{new Date(s.date).toLocaleString()}</td>
                  <td className="px-4 py-3"><span className={`px-2 py-0.5 text-xs font-bold uppercase border ${s.type === "plan" ? "text-[#00f0ff] border-[#00f0ff]/30 bg-[#00f0ff]/10" : "text-[#FFD60A] border-[#FFD60A]/30 bg-[#FFD60A]/10"}`}>{s.type === "plan" ? "Membresía" : "Producto"}</span></td>
                  <td className="px-4 py-3">{s.member_name}</td>
                  <td className="px-4 py-3 text-white/50 text-xs">{s.type === "plan" ? s.plan_name : (s.items || []).map((i) => `${i.qty}x ${i.name}`).join(", ")}</td>
                  <td className="px-4 py-3 capitalize text-white/60">{s.payment_method}</td>
                  <td className="px-4 py-3 text-right font-display text-xl text-[#39FF14]">${s.total?.toFixed(2)}</td>
                  <td className="px-4 py-3 text-right"><button data-testid={`pos-receipt-${s.id}`} onClick={() => setReceiptId(s.id)} className="p-1.5 hover:bg-white/10 text-[#00f0ff]" title="Ver recibo"><Receipt className="w-4 h-4" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <ReceiptModal saleId={receiptId} onClose={() => setReceiptId(null)} />
    </div>
  );
}
